import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import getConfig from 'next/config';
import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { Loader } from '@care/react-component-lib';
import { seoActivitySegmentationListingPage_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess as SEOSegmentationSearchResultsSuccess } from '@/__generated__/seoActivitySegmentationListingPage';
import { useResponsive } from '@/components/hooks/useResponsive';
import { LocationState } from '@/components/pages/campsAndActivities/shared/ExploreLocationSearch';
import { ActivitiesMarker } from './ActivitiesMarker';
import { ActivitiesInfoCardMobile } from './ActivitiesInfoCardMobile';

type ActivitiesMapProps = {
  activitiesSearchResults: SEOSegmentationSearchResultsSuccess['activitySearchResults'];
  userGeoInfo: string;
  slotProps?: { 'data-testid': string };
  location: LocationState;
};

const { publicRuntimeConfig } = getConfig();

const ActivitiesMap = ({
  activitiesSearchResults,
  userGeoInfo,
  slotProps,
  location,
}: ActivitiesMapProps) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: publicRuntimeConfig.GOOGLE_MAPS_KEY,
  });
  const { isSmallViewport, isMediumViewport } = useResponsive();
  const [currentMap, setCurrentMap] = useState<google.maps.Map | null>(null);
  const [centerPoint, setCenterPoint] = useState<google.maps.LatLngLiteral | undefined>(undefined);
  const [mapHeight, setMapHeight] = useState('840px');
  const [selectedMarkerId, setSelectedMarkerId] = useState<number | null>(null);
  const activitiesList = activitiesSearchResults.map((activity, idx) => ({
    ...activity.seoSegmentationActivity,
    id: idx + 1,
  }));
  const validLocations = useMemo(
    () =>
      activitiesSearchResults
        .map((activity) => activity.seoSegmentationActivity.address)
        .filter(
          (address) =>
            Number.isFinite(address?.latitude) &&
            address?.latitude !== 0 &&
            Number.isFinite(address?.longitude) &&
            address?.longitude !== 0
        ),
    [activitiesSearchResults]
  );
  const defaultMapZoom = validLocations.length > 0 ? 15 : 10;

  useEffect(() => {
    const updateMapHeight = () => {
      if (isSmallViewport) {
        // hacky solution since the map won't render without a defined height value.
        // Subtract filters height and navbar height from total viewport height
        setMapHeight('calc(100vh - 148px - 56px)');
      } else if (isMediumViewport) {
        setMapHeight('600px');
      } else {
        setMapHeight('840px');
      }
    };

    updateMapHeight();
  }, [isSmallViewport, isMediumViewport]);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    const getUserGeoLatLng = (geoInfo: string): { lat: number; lng: number } => {
      const userLatLng: { lat: number; lng: number } = { lat: 0, lng: 0 };

      geoInfo.split(',').forEach((info) => {
        const [key, value] = info.split('=');
        if (key === 'lat') {
          userLatLng.lat = parseFloat(value) || 0;
        } else if (key === 'long') {
          userLatLng.lng = parseFloat(value) || 0;
        }
      });

      return userLatLng;
    };

    const userGeoLatLng = getUserGeoLatLng(userGeoInfo);
    const latCenterPoint =
      validLocations.reduce((sum, address) => sum + (address?.latitude || 0), 0) /
        validLocations.length || 0;
    const lngCenterPoint =
      validLocations.reduce((sum, address) => sum + (address?.longitude || 0), 0) /
        validLocations.length || 0;

    // set center point to the average of all valid locations if available
    if (validLocations.length > 0 && latCenterPoint !== 0 && lngCenterPoint !== 0) {
      setCenterPoint({
        lat: latCenterPoint,
        lng: lngCenterPoint,
      });
    }
    // set center point to the zipcode's lat/lng if no valid locations are available
    // or if the center point is still 0,0
    else if (
      validLocations.length === 0 ||
      (latCenterPoint === 0 && lngCenterPoint === 0 && location?.zipcode)
    ) {
      setCenterPoint({
        lat: location?.latitude || userGeoLatLng.lat,
        lng: location?.longitude || userGeoLatLng.lng,
      });
    }
  }, [isLoaded, activitiesSearchResults, userGeoInfo, location, validLocations]);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    // extend map bounds to fit all valid locations
    if (currentMap && validLocations.length > 0) {
      const bounds = new google.maps.LatLngBounds();

      validLocations.forEach((loc) => {
        if (typeof loc?.latitude === 'number' && typeof loc?.longitude === 'number') {
          bounds.extend({ lat: loc.latitude, lng: loc.longitude });
        }
      });

      currentMap.fitBounds(bounds);
    }
  }, [currentMap, isLoaded, validLocations]);

  // render info card for mobile view
  const renderInfoCardMobile = (markerId: number | null) => {
    const activity = activitiesList.find(({ id }) => id === markerId);

    if (!isSmallViewport || !activity) {
      return null;
    }

    const { discount, price } = activity;

    const currentPrice =
      Number(discount?.price?.amount) > 0 ? Number(discount?.price?.amount) : Number(price?.amount);
    const previousPrice =
      Number(discount?.price?.amount) > 0 ? Number(price?.amount) : Number(discount?.price?.amount);

    return (
      <ActivitiesInfoCardMobile
        activity={activity}
        prevPrice={previousPrice ?? undefined}
        price={currentPrice}
        slotProps={slotProps}
      />
    );
  };

  if (!isLoaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" width="100%">
        <Loader />
      </Box>
    );
  }

  return (
    <Box {...slotProps} height="100%" width="100%">
      <GoogleMap
        id="google-maps"
        onLoad={(map) => setCurrentMap(map)}
        options={{
          draggable: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
        }}
        center={centerPoint}
        zoom={defaultMapZoom}
        mapContainerStyle={{
          height: mapHeight,
          width: 'auto',
        }}>
        {activitiesList.map((activity) => {
          return (
            <Box key={activity.id}>
              <ActivitiesMarker
                id={activity.id}
                activity={activity}
                setSelectedMarkerId={setSelectedMarkerId}
              />
            </Box>
          );
        })}
        {renderInfoCardMobile(selectedMarkerId)}
      </GoogleMap>
    </Box>
  );
};

export { ActivitiesMap };
